import {
  Badge,
  Box,
  Button,
  Table as ChakraTable,
  Code,
  Flex,
  Heading,
  HStack,
  Input,
  InputGroup,
  InputRightElement,
  Select,
  Spinner,
  Stat,
  StatLabel,
  StatNumber,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
  useDisclosure,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from "@chakra-ui/react";
import { ChangeEvent, useEffect, useRef, useState } from "react";
import { BsSearch } from "react-icons/bs";
import { FaDownload, FaUpload } from "react-icons/fa";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import Pagination from "@olagg/ui-kit/Pagination";
import {
  InventoryCode,
  InventoryCodeStatus,
  InventoryCodesUploadResponse,
  OlaggModule,
  PermissionEnum,
} from "@olagg/db-types";

import useInventoryCodes from "./hooks/useInventoryCodes";
import useProduct from "./hooks/useProduct";
import useRoles from "../../../features/Roles/hooks/useRoles";

const STATUS_LABEL: Record<InventoryCodeStatus, string> = {
  available: 'Disponible',
  delivered: 'Entregado',
  expired: 'Expirado',
};

const STATUS_COLOR: Record<InventoryCodeStatus, string> = {
  available: '#38A169',
  delivered: '#3182CE',
  expired: '#E53E3E',
};

const InventoryCodesPage = () => {
  const { id } = useParams<{ id: string }>();
  const productId = id!;
  const navigate = useNavigate();
  const { missingPermission } = useRoles();
  const { product, getProduct } = useProduct();
  const {
    codes,
    count,
    availableCount,
    totalCount,
    isLoading,
    isUploading,
    all,
    uploadCodes,
    deleteCode,
  } = useInventoryCodes(productId);

  const [searchParams, setSearchParams] = useSearchParams();
  const [filter, setFilter] = useState({
    q: searchParams.get('q') || '',
    status: (searchParams.get('status') || '') as InventoryCodeStatus | '',
    page: Number(searchParams.get('page') || 1),
    limit: Number(searchParams.get('limit') || 25),
  });

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [uploadResult, setUploadResult] = useState<InventoryCodesUploadResponse | null>(null);
  const { isOpen: isUploadResultOpen, onOpen: openUploadResult, onClose: closeUploadResult } = useDisclosure();

  useEffect(() => {
    if (productId) getProduct(productId);
  }, [productId]);

  useEffect(() => {
    all(filter);
  }, [filter]);

  const onFilter = (name: string, value: string) => {
    setFilter(prev => ({ ...prev, [name]: name === 'page' || name === 'limit' ? Number(value) : value }));
    if (value) searchParams.set(name, value);
    else searchParams.delete(name);
    setSearchParams(searchParams);
  };

  const refresh = () => all(filter);

  const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const response = await uploadCodes(file);
    if (response) {
      setUploadResult(response);
      openUploadResult();
      refresh();
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDownloadTemplate = () => {
    const rows = [
      'code,expires_at,cost',
      'STEAM-XYZ-123,2026-12-31,12.50',
      'STEAM-XYZ-124,,',
      'STEAM-XYZ-125,2027-06-30 23:59,15.00',
    ];
    const blob = new Blob([rows.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'inventory_codes_template.csv';
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  const handleDelete = async (code: InventoryCode) => {
    if (code.status !== 'available') return;
    if (!window.confirm(`¿Eliminar el código ${code.code}?`)) return;
    const ok = await deleteCode(code.id);
    if (ok) refresh();
  };

  const formatDate = (date: string | null) => {
    if (!date) return '-';
    return new Date(date).toLocaleString('es-ES', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const canEdit = !missingPermission(OlaggModule.Product, PermissionEnum.EDIT);

  return (
    <Flex direction="column" w="full" color="white" px={6} py={4} gap={4}>
      <Flex justify="space-between" align="center">
        <VStack align="start" spacing={0}>
          <Heading size="lg">Inventario de códigos</Heading>
          {product && (
            <Text fontSize="sm" color="gray.300">
              {product.name} <Code fontSize="xs" colorScheme="purple">{product.source}</Code>
            </Text>
          )}
        </VStack>
        <Button variant="outline" onClick={() => navigate('/products')}>
          Volver a productos
        </Button>
      </Flex>

      <HStack spacing={6}>
        <Stat bg="gray.700" p={3} borderRadius="md" maxW="200px">
          <StatLabel>Disponibles</StatLabel>
          <StatNumber color="green.300">{availableCount}</StatNumber>
        </Stat>
        <Stat bg="gray.700" p={3} borderRadius="md" maxW="200px">
          <StatLabel>Totales</StatLabel>
          <StatNumber>{totalCount}</StatNumber>
        </Stat>
      </HStack>

      <Flex justify="space-between" gap={4} wrap="wrap">
        <HStack flex="1" gap={4}>
          <InputGroup w="40%">
            <Input
              placeholder="Buscar por código"
              value={filter.q}
              onChange={(e) => onFilter('q', e.target.value)}
            />
            <InputRightElement children={<BsSearch />} />
          </InputGroup>
          <Select
            placeholder="Todos los estados"
            value={filter.status}
            onChange={(e) => onFilter('status', e.target.value)}
            w="25%"
          >
            <option value="available" style={{ backgroundColor: '#2D3748' }}>Disponibles</option>
            <option value="delivered" style={{ backgroundColor: '#2D3748' }}>Entregados</option>
            <option value="expired" style={{ backgroundColor: '#2D3748' }}>Expirados</option>
          </Select>
        </HStack>

        <HStack>
          <Button
            leftIcon={<FaDownload />}
            variant="outline"
            onClick={handleDownloadTemplate}
          >
            Descargar template
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,.xlsx,.xls"
            style={{ display: 'none' }}
            onChange={handleFileChange}
          />
          <Button
            leftIcon={<FaUpload />}
            variant="outlinedGradient"
            isDisabled={!canEdit}
            isLoading={isUploading}
            loadingText="Subiendo"
            onClick={() => fileInputRef.current?.click()}
          >
            Subir CSV / XLSX
          </Button>
        </HStack>
      </Flex>

      <Text fontSize="xs" color="gray.400">
        Formato esperado: headers <Code fontSize="xs">code</Code>, <Code fontSize="xs">expires_at</Code> (opcional),{" "}
        <Code fontSize="xs">cost</Code> (opcional). Máximo 50.000 filas por archivo.
      </Text>

      <TableContainer overflowX="auto" overflowY="auto" maxH="60vh">
        <ChakraTable variant="striped" size="sm" colorScheme="olaggPurple" fontSize="14px">
          <Thead>
            <Tr position="sticky" bg="gray.800" top="0" zIndex="100">
              <Th color="white">ID</Th>
              <Th color="white">Código</Th>
              <Th color="white">Estado</Th>
              <Th color="white">Costo</Th>
              <Th color="white">Expira</Th>
              <Th color="white">Entregado</Th>
              <Th color="white">Usuario</Th>
              <Th color="white">Orden</Th>
              <Th color="white">Creado</Th>
              <Th color="white">Acciones</Th>
            </Tr>
          </Thead>
          <Tbody>
            {isLoading ? (
              <Tr><Td colSpan={10}><Flex justify="center" py={6}><Spinner /></Flex></Td></Tr>
            ) : codes.length === 0 ? (
              <Tr><Td colSpan={10}><Text color="gray.400" textAlign="center" py={6}>Sin códigos para mostrar</Text></Td></Tr>
            ) : (
              codes.map((code) => (
                <Tr key={code.id} color="white">
                  <Td>{code.id}</Td>
                  <Td><Code colorScheme="green">{code.code}</Code></Td>
                  <Td>
                    <Badge bg={STATUS_COLOR[code.status]} color="white" px={2} py={1} borderRadius="md">
                      {STATUS_LABEL[code.status] || code.status}
                    </Badge>
                  </Td>
                  <Td>{code.cost ?? '-'}</Td>
                  <Td>{formatDate(code.expiresAt)}</Td>
                  <Td>{formatDate(code.deliveredAt)}</Td>
                  <Td>
                    {code.user ? (
                      <VStack align="start" spacing={0}>
                        <Text fontSize="xs">{code.user.name}</Text>
                        <Text fontSize="xs" color="gray.400">{code.user.email}</Text>
                      </VStack>
                    ) : '-'}
                  </Td>
                  <Td>{code.orderId ?? '-'}</Td>
                  <Td>{formatDate(code.createdAt)}</Td>
                  <Td>
                    <Button
                      size="xs"
                      bgColor="#E53E3E"
                      isDisabled={!canEdit || code.status !== 'available'}
                      onClick={() => handleDelete(code)}
                    >
                      Eliminar
                    </Button>
                  </Td>
                </Tr>
              ))
            )}
          </Tbody>
        </ChakraTable>
      </TableContainer>

      <Flex w="full" justifyContent="center">
        <Pagination
          params={{ limit: filter.limit, page: filter.page, count }}
          onClick={(newPage) => onFilter('page', String(newPage + 1))}
          onLimitChange={(newLimit) => onFilter('limit', newLimit)}
          hideLimitInput
        />
      </Flex>

      <Modal isOpen={isUploadResultOpen} onClose={closeUploadResult} size="2xl" isCentered>
        <ModalOverlay />
        <ModalContent bg="gray.800" color="white">
          <ModalHeader>Resultado de la importación</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {uploadResult && (
              <VStack align="stretch" spacing={3}>
                <HStack justify="space-between">
                  <Text color="gray.300">Importados:</Text>
                  <Badge colorScheme="green">{uploadResult.imported}</Badge>
                </HStack>
                <HStack justify="space-between">
                  <Text color="gray.300">Saltados:</Text>
                  <Badge colorScheme={uploadResult.skipped?.length ? 'red' : 'gray'}>
                    {uploadResult.skipped?.length || 0}
                  </Badge>
                </HStack>
                <HStack justify="space-between">
                  <Text color="gray.300">Disponibles ahora:</Text>
                  <Text fontWeight="700">{uploadResult.availableCount ?? uploadResult.available_count ?? '-'}</Text>
                </HStack>
                <HStack justify="space-between">
                  <Text color="gray.300">Totales ahora:</Text>
                  <Text fontWeight="700">{uploadResult.totalCount ?? uploadResult.total_count ?? '-'}</Text>
                </HStack>

                {uploadResult.skipped && uploadResult.skipped.length > 0 && (
                  <Box mt={2} p={3} bg="gray.900" borderRadius="md" maxH="240px" overflowY="auto">
                    <Text color="gray.300" mb={2}>Detalle de saltados:</Text>
                    {uploadResult.skipped.map((s, i) => (
                      <Text key={i} fontSize="xs" fontFamily="mono" color="orange.200">
                        Fila {s.row} — {s.code ?? '(sin código)'} — {s.reason}
                      </Text>
                    ))}
                  </Box>
                )}
              </VStack>
            )}
          </ModalBody>
          <ModalFooter>
            <Button onClick={closeUploadResult}>Cerrar</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Flex>
  );
};

export default InventoryCodesPage;
